import Canvas from 'editor/components/Canvas';
import Cursor, { CursorTypes } from 'editor/components/Cursor';
import DrawTempRectInteraction from 'editor/components/interactions/DrawTempRectInteraction';
import Markup from 'editor/components/Markup';
import Overlay from 'editor/components/Overlay';
import RFIPost from 'editor/components/RFIPost';
import SheetEvents from 'editor/components/SheetEvents';
import Tool, { EditorTools } from 'editor/components/Tool';
import ToolOverlay from 'editor/components/ToolOverlay';
import { Point, Rect } from 'editor/geometry';
import noop from 'lodash/noop';
import { Markup as MarkupRecord } from 'records/Markup';
import { v4 as uuidv4 } from 'uuid';
import { MouseDownArgs, MouseMoveArgs, MouseUpArgs } from './index';

function mouseDown({ hit, world }: MouseDownArgs): void {
  world.grabAll(RFIPost).forEach(() => {
    // NOTE: Currently there is a bug in MarkupKit which crashes the browser page
    //       if a free text type markup is used, and its size goes too small.
    //       making it not suitable for use as an intermediate drawn markup.
    //       Using a standard rect markup for the time being.

    const toolOverlayEntity = world.locate([Overlay, ToolOverlay]);
    if (!toolOverlayEntity) return;

    const overlayComp = toolOverlayEntity.get(Overlay);
    const tempCanvasComp = new Canvas();
    tempCanvasComp.canvas = overlayComp.overlayCanvas;

    world
      .createEntity()
      .add(tempCanvasComp)
      .add(
        new DrawTempRectInteraction({
          originX: hit.x,
          originY: hit.y,
          currentX: hit.x,
          currentY: hit.y,
          stroke: `#0000FF`,
          strokeWidth: 3,
        })
      );
  });
}

function mouseMove({ world, hit }: MouseMoveArgs): void {
  // Should always be crosshair cursor for now.
  world.grabAll(Cursor).forEach(({ component }) => {
    component.cursor = CursorTypes.Crosshair;
  });

  const dtri = world.locate(DrawTempRectInteraction);

  if (dtri) {
    const interaction = dtri.get(DrawTempRectInteraction);

    interaction.currentX = hit.x;
    interaction.currentY = hit.y;
  }
}

function mouseUp({ world, hit, markupKit }: MouseUpArgs): void {
  const rfiPostEntity = world.locate(RFIPost);

  if (!rfiPostEntity) return;

  const rfiPostComp = rfiPostEntity.get(RFIPost);

  world.grabAll(DrawTempRectInteraction).forEach(({ entity, component }) => {
    if (hit.x !== component.originX && hit.y !== component.originY) {
      const { MKMarkup, MKSpan, MKBorderStyle } = markupKit;
      const { originX, originY, currentX, currentY } = component;
      const p1 = new Point(originX, originY);
      const p2 = new Point(currentX, currentY);
      const box = Rect.fromPoints(p1, p2);

      const rfiMarkupText =
        rfiPostComp.rfi.versionnumber === 0
          ? `RFI #${rfiPostComp.rfi.number}`
          : `RFI #${rfiPostComp.rfi.number}.${rfiPostComp.rfi.versionnumber} ${
              rfiPostComp.rfi.answer === null ? '\r' : ''
            }`;

      const mkMarkup = MKMarkup.createFreeText();
      mkMarkup.setWidth(3);
      mkMarkup.setFillOpacity(0);
      mkMarkup.setStrokeColor(1, 0, 0);
      mkMarkup.setRectDiff(15, 15, 15, 15);

      mkMarkup.setBorderEffectIntensity(2);
      mkMarkup.setBorderEffectStyle(MKBorderStyle.Cloudy);

      mkMarkup.setSubject(rfiMarkupText);

      mkMarkup.setRect(box.left, box.bottom, box.right, box.top);

      const content = new MKSpan();
      content.setText(rfiMarkupText);
      content.setTextSize(18);
      content.setFontWeight(700);
      content.setAlignment('center');
      content.setVerticalAlignment('middle');
      mkMarkup.setRichContents(content);

      const richDefaults = new MKSpan();
      richDefaults.setAlignment('center');
      richDefaults.setVerticalAlignment('middle');
      mkMarkup.setRichDefaults(richDefaults);

      const creationDate = new Date().toISOString();
      mkMarkup.setCreationDate(creationDate);
      mkMarkup.setModDate(creationDate);

      const openText = {
        alignment: 'center',
        color: ['RGB', 1, 0, 0],
        fontFamily: ['Helvetica'],
        fontStretch: 'normal',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 13.8,
        margin: 0,
        marginSet: false,
        strikeThrough: false,
        subscript: false,
        superscript: false,
        text: '*Open',
        textSize: 18,
        underline: false,
        verticalAlignment: 'middle',
      };

      const markupObject = mkMarkup.getAsObject();
      if (rfiPostComp.rfi.answer === null) {
        markupObject.richContents = [...markupObject.richContents, openText];
      }
      const stringifiedMarkup = JSON.stringify(markupObject);
      const markup = new MarkupRecord({
        markupid: uuidv4(),
        fileid: rfiPostComp.pageid,
        projectid: rfiPostComp.rfi.projectid,
        jsonBlob: stringifiedMarkup,
      });

      const markupComp = new Markup();
      markupComp.isRFI = true;
      markupComp.isOpen = rfiPostComp.rfi.answer === null;
      markupComp.mkMarkup = mkMarkup;
      if (rfiPostComp.rfi.answer === null) {
        markupComp.markup = markup;
      }

      world.grabAll(SheetEvents).forEach(({ component: comp }) => {
        comp.emit('create:rfi-post', { markupComp });
      });
    }

    world.grabAll<Tool<EditorTools>>(Tool).forEach(({ component: tool }) => {
      tool.tool = EditorTools.Pointer;
    });

    entity.destroy();
  });
}

function dblclick(): void {
  console.error('postRFIEvents.dblclick: unimplemented');
}

export default {
  mouseDown,
  mouseMove,
  mouseUp,
  click: noop,
  dblclick,
};
